/**
 * Retrieving a document from a project record — the browser half of canon R4.
 *
 * `GET /projects/:code/documents/:documentId/download` shipped in #664 and **nothing linked to it**, so
 * R4 ("employees retrieve files through the PEE, and retrieval is attributable") was a route rather than
 * a capability: the Hub could take a file in and list it and still could not hand it back. This is the
 * click.
 *
 * ## Why a NAVIGATION and not a fetch
 *
 * The route answers 302 to a short-lived presigned URL on the document store. A top-level navigation
 * follows that redirect with no CORS involvement and never brings the credential into JS — which is the
 * same reason the route hands back a redirect instead of a body (`retrieval.ts`: the URL "is never
 * logged, never persisted, never returned in a body"). Fetching it would need the store's bucket to
 * allow our origin, and would put a 15-minute credential in a variable for no gain.
 *
 * ## ⚠️ Why the session is checked FIRST
 *
 * The access cookie lives 15 minutes; the refresh chain lives an hour. The SPA renews transparently
 * (`api.ts` → `request`), but only when an XHR 401s — and a plain link is not an XHR. Someone reading a
 * project record for twenty minutes and then clicking a filename would navigate straight into a raw
 * `401` JSON page, which teaches them the product is broken rather than that their session lapsed. So
 * the click makes one credentialed call first, which renews the cookie if it needs renewing, and only
 * then navigates.
 *
 * The pieces the browser provides are injected rather than reached for, so every branch below is
 * testable — the web package has no jsdom, and an untestable branch in the one path that makes an audit
 * requirement real is not a trade worth making.
 */

import { apiBaseUrl } from './config.js';

/** The attributable-retrieval route for one document on one project record. */
export function documentDownloadUrl(code: string, documentId: string): string {
  return `${apiBaseUrl}/projects/${encodeURIComponent(code)}/documents/${encodeURIComponent(
    documentId,
  )}/download`;
}

/**
 * A drawing sheet's file AS FILED — the version that stood when its issue was filed (ADR-0061 D4).
 * Keyed by the sheet VERSION, not a document id: which file is "as filed" is the api's decision, made
 * from the record, and never a document id the browser chose.
 */
export function sheetAsFiledUrl(code: string, versionId: string): string {
  return `${apiBaseUrl}/projects/${encodeURIComponent(code)}/drawings/versions/${encodeURIComponent(
    versionId,
  )}/as-filed`;
}

/**
 * The link a person SHARES with a colleague — canon R9's `share` verb.
 *
 * ## ⚠️ IT POINTS AT THE PEE, NEVER AT THE STORE, AND THAT IS A CANON CONSTRAINT
 *
 * R3: the physical store "is not reachable by any human account — **not a mapped drive, not a share,
 * not a browsable path**". R4: "employees retrieve files through the PEE, and **retrieval is
 * attributable**". A share that handed over a store URL would break both at once: it would be the
 * browsable path R3 forbids, and the recipient's retrieval would be recorded against the sharer, or
 * against nobody. So this is a link to the RECORD — the recipient signs in as themselves, and their
 * click writes their own row in `document_retrievals`.
 *
 * It is deliberately NOT `documentDownloadUrl`. That route is attributable, so sharing it would not
 * lose the audit trail — but it starts a download into a colleague's browser with no context, and the
 * thing a person actually wants to send is "here is the current geotech report, on the record".
 *
 * ## Why the origin is a parameter
 *
 * Same reason the browser pieces below are injected: reaching for `window.location.origin` here would
 * make the one function whose output leaves the product and lands in somebody's email untestable.
 *
 * @param origin  The Hub's own origin, e.g. `https://hub.staging.ggpathos.com`.
 * @param projectRef  The project CODE, or the record's uuid when it has none — a pursuit carries no
 *   number (canon R5/R-e), and a link built from the code alone reads `/projects/null/…`.
 */
export function documentShareUrl(origin: string, projectRef: string, documentId: string): string {
  const base = origin.replace(/\/+$/, '');
  return `${base}/projects/${encodeURIComponent(projectRef)}?doc=${encodeURIComponent(
    documentId,
  )}#documents`;
}

/** The share link for a whole record, when the thing being sent is the project rather than a file. */
export function projectShareUrl(origin: string, projectRef: string): string {
  return `${origin.replace(/\/+$/, '')}/projects/${encodeURIComponent(projectRef)}`;
}

/**
 * A tab already opened by the click. Structural on purpose — a real `Window` satisfies it, and so does a
 * test double, without either pretending to be the whole DOM.
 */
export interface OpenedTab {
  location: { replace: (url: string) => void };
  close: () => void;
  opener: unknown;
}

export interface RetrievalBrowser {
  /**
   * Open a blank tab. ⚠️ Must be called INSIDE the click handler's synchronous prologue: a tab opened
   * after an `await` has left the user-gesture context and popup blockers eat it. Returns `null` when
   * the browser blocks it anyway, which is a supported outcome, not a failure.
   */
  openTab: () => OpenedTab | null;
  /** Make one credentialed call, renewing the session if it has lapsed. False iff the session is gone. */
  ensureSession: () => Promise<boolean>;
  /** Navigate the CURRENT tab — the fallback when no tab could be opened. */
  goTo: (url: string) => void;
}

export type RetrievalOutcome =
  /** The document store's URL was handed to a tab. The api recorded WHO before it answered. */
  | 'served'
  /** The session could not be renewed; the caller should route to sign-in. Nothing was opened. */
  | 'signed-out';

export async function retrieveDocument(
  browser: RetrievalBrowser,
  code: string,
  documentId: string,
): Promise<RetrievalOutcome> {
  return retrieveUrl(browser, documentDownloadUrl(code, documentId));
}

/**
 * Open any attributable-retrieval route the same way — the tab first, the session next, then the
 * navigation. One function, so a second way to open a file cannot drift from the first.
 */
export async function retrieveUrl(
  browser: RetrievalBrowser,
  url: string,
): Promise<RetrievalOutcome> {
  const tab = browser.openTab();
  // Sever the handle before the tab goes anywhere. The redirect's destination is the document store, not
  // us, and a foreign page holding `window.opener` can navigate the tab it came from.
  if (tab) tab.opener = null;

  if (!(await browser.ensureSession())) {
    // Close what the click opened. A blank tab left behind after being bounced to sign-in reads as the
    // download having half-happened.
    tab?.close();
    return 'signed-out';
  }

  if (tab) tab.location.replace(url);
  else browser.goTo(url);
  return 'served';
}
